// Durable per-purchase-option measurement profile.
//
// The profile answers, once and durably: what does ONE of this option's
// purchase packages physically contain, in the objective unit this product
// compares in? It lives at conversion_snapshot.measurement_profile — the
// fourth tenant of that jsonb, beside the deterministic conversion record,
// ai_objective (legacy), package_interpretation and price_comparison — so no
// migration is needed and every existing snapshot writer's sibling-key
// preservation carries it along.
//
// Why it exists: price_comparison snapshots are relationship-scoped and
// fingerprint-invalidated, so the whole-relationship AI bridge was re-asked
// from scratch on every recompute. A measurement profile belongs to the
// OPTION, survives relationship recomputes, and lets vendor comparison be
// arithmetic whenever every option carries one. The relationship AI becomes
// a fallback for genuinely unresolved/conflicting packages.
//
// Provenance vocabulary (task-level requirement — explicit vs deterministic
// vs estimated must stay distinguishable in storage and UI):
//   'explicit'      — the quantity is printed in the package text and was
//                     read exactly (deterministic parse, or AI reading a
//                     printed figure the parser's grammar missed).
//   'deterministic' — derived arithmetically from structured facts
//                     (inner-pack columns, container identity).
//   'ai_estimate'   — a trade estimate for an unprinted quantity.
//   'user'          — human-entered/confirmed. Never overwritten by software.
//
// Pure ESM, no I/O. Unit-tested under node --test.

export const MEASUREMENT_PROFILE_VERSION = 1;

export const MEASUREMENT_PROFILE_SOURCES = Object.freeze([
  "explicit", "deterministic", "ai_estimate", "user",
]);

// Higher rank never gets overwritten by lower rank.
const SOURCE_RANK = { user: 3, explicit: 2, deterministic: 1, ai_estimate: 0 };

const FAMILIES = new Set(["mass", "volume", "count"]);

function finiteOrNull(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function positiveOrNull(value) {
  const n = finiteOrNull(value);
  return n != null && n > 0 ? n : null;
}

function normalizeRawText(value) {
  return String(value ?? "").trim().replace(/\s+/g, " ").toLowerCase();
}

/**
 * The package wording a profile interpreted. A profile stands while this
 * signature is unchanged and goes stale the moment the vendor's wording or
 * purchase unit materially changes — the same reuse convention
 * package_interpretation uses (storedInterpretationReusable).
 */
export function packageSignatureForOption(option) {
  return {
    pack_description: normalizeRawText(option?.pack_description),
    purchase_unit: normalizeRawText(option?.purchase_unit),
  };
}

/**
 * Build the snapshot value stored at conversion_snapshot.measurement_profile.
 */
export function buildMeasurementProfile({
  family, unit, quantityPerPackage, quantityPerEach = null, packageCount = null,
  source, statedInText = false, confidence = null, reasoning = null,
  model = null, verified = false, option = null, calculatedAt = null,
}) {
  if (!FAMILIES.has(family)) return null;
  const qty = positiveOrNull(quantityPerPackage);
  const unitText = String(unit ?? "").trim();
  if (qty == null || !unitText) return null;
  const src = MEASUREMENT_PROFILE_SOURCES.includes(source) ? source : "ai_estimate";
  return {
    version: MEASUREMENT_PROFILE_VERSION,
    family,
    unit: unitText,
    quantity_per_package: qty,
    quantity_per_each: positiveOrNull(quantityPerEach),
    package_count: positiveOrNull(packageCount),
    source: src,
    stated_in_text: statedInText === true,
    confidence: finiteOrNull(confidence),
    reasoning: reasoning ? String(reasoning).slice(0, 500) : null,
    model: model ? String(model).slice(0, 80) : null,
    verified: verified === true || src === "user",
    raw: packageSignatureForOption(option),
    calculated_at: calculatedAt ?? null,
  };
}

/**
 * Read and validate an option's stored measurement profile. Returns the
 * normalized profile or null. `requireCurrent` (default true) additionally
 * requires the stored raw signature to match the option's CURRENT wording —
 * a stale profile reads as absent, never as a fact about the new package.
 */
export function readMeasurementProfile(option, { requireCurrent = true } = {}) {
  const snap = option?.conversion_snapshot;
  const profile = snap && typeof snap === "object" ? snap.measurement_profile : null;
  if (!profile || typeof profile !== "object") return null;
  if (!FAMILIES.has(profile.family)) return null;
  const qty = positiveOrNull(profile.quantity_per_package);
  const unit = String(profile.unit ?? "").trim();
  if (qty == null || !unit) return null;
  if (requireCurrent && !profileMatchesOption(profile, option)) return null;
  return {
    family: profile.family,
    unit,
    quantityPerPackage: qty,
    quantityPerEach: positiveOrNull(profile.quantity_per_each),
    packageCount: positiveOrNull(profile.package_count),
    source: MEASUREMENT_PROFILE_SOURCES.includes(profile.source) ? profile.source : "ai_estimate",
    statedInText: profile.stated_in_text === true,
    confidence: finiteOrNull(profile.confidence),
    reasoning: profile.reasoning ?? null,
    model: profile.model ?? null,
    verified: profile.verified === true,
    raw: profile.raw ?? null,
    calculatedAt: profile.calculated_at ?? null,
  };
}

/** True when the stored profile still describes the option's current package
 *  wording. A profile with no recorded raw signature (hand-written rows,
 *  user overrides) is treated as current — humans outrank wording drift. */
export function profileMatchesOption(profile, option) {
  const raw = profile?.raw;
  if (!raw || typeof raw !== "object") {
    return profile?.source === "user" || profile?.verified === true;
  }
  const current = packageSignatureForOption(option);
  return normalizeRawText(raw.pack_description) === current.pack_description
    && normalizeRawText(raw.purchase_unit) === current.purchase_unit;
}

/**
 * May `incoming` replace `existing`? Encodes the caching/provenance rules:
 * - a user/verified profile is never overwritten by software;
 * - a stale profile (signature no longer matching the option) always loses;
 * - otherwise higher-ranked provenance wins, and equal provenance upgrades
 *   only on strictly higher confidence (later stronger evidence improves an
 *   AI estimate; an equal-or-weaker one changes nothing).
 */
export function shouldReplaceProfile(existing, incoming, option = null) {
  if (!incoming) return false;
  if (!existing || typeof existing !== "object") return true;
  if (existing.verified === true || existing.source === "user") return false;
  if (option && !profileMatchesOption(existing, option)) return true;
  const existingRank = SOURCE_RANK[existing.source] ?? 0;
  const incomingRank = SOURCE_RANK[incoming.source] ?? 0;
  if (incomingRank > existingRank) return true;
  if (incomingRank < existingRank) return false;
  const existingConf = finiteOrNull(existing.confidence) ?? 0;
  const incomingConf = finiteOrNull(incoming.confidence) ?? 0;
  return incomingConf > existingConf;
}

/**
 * Merge a new profile into a conversion_snapshot object (never mutating the
 * input), respecting shouldReplaceProfile. Returns the merged snapshot, or
 * null when the existing profile stands (nothing to write).
 */
export function mergeMeasurementProfile(snapshot, profile, option = null) {
  const base = snapshot && typeof snapshot === "object" && !Array.isArray(snapshot) ? { ...snapshot } : {};
  if (!shouldReplaceProfile(base.measurement_profile, profile, option)) return null;
  base.measurement_profile = profile;
  return base;
}

/**
 * Profile from the combined identity pass's coerced measurement block
 * (aiItemMatch.mjs coerceItemIdentityResults). A printed quantity the model
 * read is 'explicit'; anything else is an 'ai_estimate'.
 */
export function profileFromMeasurementBlock(block, { model = null, option = null, calculatedAt = null } = {}) {
  if (!block || typeof block !== "object") return null;
  return buildMeasurementProfile({
    family: block.comparison_family,
    unit: block.comparison_unit,
    quantityPerPackage: block.quantity_per_package,
    quantityPerEach: block.quantity_per_each,
    packageCount: block.package_count,
    source: block.stated_in_text === true ? "explicit" : "ai_estimate",
    statedInText: block.stated_in_text === true,
    confidence: block.confidence,
    reasoning: block.reasoning,
    model,
    option,
    calculatedAt,
  });
}

// ── Recipe-side hint ────────────────────────────────────────────────────────
// Menu & Recipes' unit engine bridges count ↔ mass/volume through a
// conversion's `each_size` ({ qty, unit }). A purchase-option profile that
// knows the per-each bridge (one ear ≈ 0.5 lb) can seed that fact so a
// recipe's "2 ears corn" costs without a fresh AI question. This is a HINT
// for conversion creation only — recipe-form yields (chopped, trimmed,
// cooked) remain culinary facts the recipe side owns, and nothing here may
// overwrite stored or user-verified physics.

/**
 * The best per-each bridge available across an item's options' measurement
 * profiles: `{ qty, unit, confidence, source }` in the profile's objective
 * unit, or null. Prefers user > explicit > deterministic > ai_estimate, then
 * higher confidence.
 */
export function eachSizeHintFromOptions(options) {
  let best = null;
  for (const option of Array.isArray(options) ? options : []) {
    const profile = readMeasurementProfile(option);
    if (!profile || profile.quantityPerEach == null) continue;
    if (profile.family !== "mass" && profile.family !== "volume") continue;
    const rank = SOURCE_RANK[profile.source] ?? 0;
    const conf = profile.confidence ?? 0;
    if (!best || rank > best.rank || (rank === best.rank && conf > best.confidence)) {
      best = {
        qty: profile.quantityPerEach,
        unit: profile.unit,
        confidence: conf,
        source: profile.source,
        rank,
      };
    }
  }
  if (!best) return null;
  const { rank: _rank, ...hint } = best;
  return hint;
}

/**
 * The best WHOLE-PACKAGE measurement across an item's options' profiles:
 * `{ family, unit, quantityPerPackage, quantityPerEach, packageCount,
 *    confidence, source, packDescription }`, or null.
 *
 * Where eachSizeHintFromOptions carries only the per-piece bridge, this
 * carries everything a profile objectified about one purchase package — so a
 * pack wording NO deterministic parser reads ("6 x 2/4 OZ") can still become
 * a usable conversion from the measurement the purchasing side already
 * established and stored. Same ranking: user > explicit > deterministic >
 * ai_estimate, then higher confidence. A profile that knows only the family
 * and total quantity still wins over nothing — the per-each bridge and
 * package count ride along when known.
 *
 * This is a HINT for the consumer's own gap-filling rules; nothing here may
 * overwrite stored or user-verified facts on the consuming side.
 */
export function packContentsHintFromOptions(options) {
  let best = null;
  for (const option of Array.isArray(options) ? options : []) {
    const profile = readMeasurementProfile(option);
    if (!profile) continue;
    const rank = SOURCE_RANK[profile.source] ?? 0;
    const conf = profile.confidence ?? 0;
    if (!best || rank > best.rank || (rank === best.rank && conf > best.confidence)) {
      best = {
        family: profile.family,
        unit: profile.unit,
        quantityPerPackage: profile.quantityPerPackage,
        quantityPerEach: profile.quantityPerEach,
        packageCount: profile.packageCount,
        confidence: conf,
        source: profile.source,
        packDescription: option?.pack_description ?? null,
        rank,
      };
    }
  }
  if (!best) return null;
  const { rank: _rank, ...hint } = best;
  return hint;
}
